/* Theme custom file */
import { Component } from '@theme/component';

/**
 * Category quick-filter bar for the collection grid: owns the scrolling
 * affordances of the category pills.
 *
 * The pills scroll horizontally in their own scrollport and the filter
 * drawer trigger sits outside it, so nothing can slide underneath the
 * trigger. This component only adds the affordance: a prev/next arrow shown
 * while there is more to scroll on that side, published as the
 * `scrollable-prev` / `scrollable-next` attributes the section's CSS reads.
 * Without JS the list still scrolls by hand, the arrows just never show.
 *
 * State lives in DOM attributes rather than private fields because a facets
 * re-render morphs this element in place without disconnecting it (same
 * lesson as collection-sticky-bar.js). Watching our own attributes lets the
 * arrows re-assert themselves right after a morph strips them.
 *
 * @extends {Component}
 */
class CollectionQuickFilters extends Component {
  /** Sub-pixel scroll positions would otherwise never count as an edge. */
  static EDGE_TOLERANCE = 1;

  /** Share of the visible width moved per arrow click. */
  static SCROLL_STEP = 0.7;

  #resizeObserver = new ResizeObserver(() => this.#update());
  #attributeObserver = new MutationObserver(() => this.#update());

  connectedCallback() {
    super.connectedCallback();

    const list = this.#list;
    if (!list) return;

    list.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver.observe(list);
    this.#attributeObserver.observe(this, {
      attributes: true,
      attributeFilter: ['scrollable-prev', 'scrollable-next'],
    });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#list?.removeEventListener('scroll', this.#update);
    this.#resizeObserver.disconnect();
    this.#attributeObserver.disconnect();
  }

  scrollPrev() {
    this.#scrollBy(-1);
  }

  scrollNext() {
    this.#scrollBy(1);
  }

  /** Facette collections render no list at all, only the drawer trigger. */
  get #list() {
    return this.refs.list instanceof HTMLElement ? this.refs.list : null;
  }

  /** @param {number} direction -1 towards the start, 1 towards the end. */
  #scrollBy(direction) {
    const list = this.#list;
    if (!list) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollBy({
      left: direction * list.clientWidth * CollectionQuickFilters.SCROLL_STEP,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  #update = () => {
    const list = this.#list;
    if (!list) return;

    const { scrollLeft, clientWidth, scrollWidth } = list;
    const tolerance = CollectionQuickFilters.EDGE_TOLERANCE;
    const prev = scrollLeft > tolerance;
    const next = scrollLeft + clientWidth < scrollWidth - tolerance;

    this.#setArrow('scrollable-prev', prev, this.refs.prev, this.refs.next);
    this.#setArrow('scrollable-next', next, this.refs.next, this.refs.prev);
  };

  /**
   * The DOM attribute is the change guard, so a re-fire from our own
   * MutationObserver finds nothing to do. An arrow that hides while focused
   * would drop keyboard focus on <body>, hand it to the other arrow instead.
   *
   * @param {string} attribute
   * @param {boolean} enabled
   * @param {unknown} arrow
   * @param {unknown} fallback
   */
  #setArrow(attribute, enabled, arrow, fallback) {
    if (enabled === this.hasAttribute(attribute)) return;

    if (!enabled && arrow instanceof HTMLElement && document.activeElement === arrow) {
      if (fallback instanceof HTMLElement) fallback.focus();
    }
    this.toggleAttribute(attribute, enabled);
  }
}

if (!customElements.get('collection-quick-filters')) {
  customElements.define('collection-quick-filters', CollectionQuickFilters);
}
